import { useEffect, useState } from "react";
import InventoryMovementService from "../../../api/Inventory/InventoryMovementService";
import { buildVisitTrackingRelationId, dateToSequenceNumber } from "../../../api/Common/Util";

export default function usePriorProtocolDispenses({ protocolId = 0, medicalRecordNumber = null, visitDate = null, currentMovementId = 0, persistedSignature = '' } = {}) {
    const [comprobantes, setComprobantes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const relatedId = protocolId && medicalRecordNumber
        ? buildVisitTrackingRelationId(medicalRecordNumber, protocolId)
        : null;
    const currentSequenceNumber = dateToSequenceNumber(visitDate);

    useEffect(() => {
        if (!relatedId || !currentSequenceNumber) {
            setComprobantes([]);
            return;
        }

        const controller = new AbortController();
        let active = true;

        async function fetchPriorDispenses() {
            setIsLoading(true);
            setComprobantes([]);
            try {
                const result = await InventoryMovementService.PriorProtocolDispenses({
                    relatedId, currentSequenceNumber, currentMovementId: currentMovementId ?? 0, signal: controller.signal
                });
                if (active) setComprobantes(toComprobantes(result.Value ?? []));
            } catch (err) {
                if (active && err?.name !== 'CanceledError' && err?.name !== 'AbortError') setComprobantes([]);
            } finally {
                if (active) setIsLoading(false);
            }
        }

        fetchPriorDispenses();

        return () => {
            active = false;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relatedId, currentSequenceNumber, currentMovementId, persistedSignature]);

    return [comprobantes, isLoading];
}

function toComprobantes(items) {
    const groups = new Map();
    for (const raw of items) {
        const key = raw.originDispenseMovementId;
        if (!groups.has(key)) {
            groups.set(key, {
                originDispenseMovementId: key,
                sequenceNumber: raw.sequenceNumber,
                items: []
            });
        }
        const perUnitTotal = raw.dispensedKitQuantity > 0 ? raw.dispensedKitQuantity : raw.quantity;
        const unitCount = perUnitTotal > 0 ? Math.max(1, Math.ceil(raw.quantity / perUnitTotal)) : 1;
        groups.get(key).items.push({
            originMoveItemId: raw.id,
            item: raw.item,
            origin: raw.origin ?? raw.item?.origin ?? null,
            kitNumber: raw.kitNumber ?? raw.assignment?.kitNumber ?? null,
            release: raw.release,
            assignment: raw.assignment ?? raw.item?.assignment ?? null,
            presentation: raw.presentation ?? raw.item?.presentation,
            perUnitTotal,
            dispensedKitQuantity: raw.dispensedKitQuantity ?? null,
            unitCount
        });
    }

    return Array.from(groups.values()).map(c => ({
        ...c,
        summary: c.items.map(i => `${i.presentation} (${i.perUnitTotal})`).join(', ')
    }));
}
